'use client';

import styles from './RecommendationPage.module.scss';
import {
  createBrowseUser,
  recommendPdpPlansAction,
  recommendMaPlansAction,
  recommendMedSuppPlansAction,
  updateMedApiUser,
} from 'actions/Plans';
import Loading from 'components/Loading';
import { useAppDispatch, useAppSelector, useAppStore } from 'lib/hooks';
import { useRouter, useSearchParams } from 'next/navigation';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DEFAULT_PLAN_YEAR, SUPPORTED_PLAN_YEARS } from 'lib/planYears';
import { planIdentityKey } from 'utils/selectedPlans';
import { setSelectedPlanYear } from 'slices/availabilitySlice';
import { MedicareApiPlan } from 'types/api';
import PlanCard from '../PlanCard';
import { selectCurrentLocation } from 'slices/locationSlice';
import { selectUser } from 'slices/userSlice';
import CTASideAd from 'components/CTASideAd';
import {
  RecommendationResults,
  selectRecommendationGapPlans,
  selectRecommendationMaPlans,
  selectRecommendationPdpPlans,
  setGapPlans,
  setMaPlans,
  setPdpPlans,
} from 'slices/recommendationSlice';
import PlanYearToggle from './PlanYearToggle';
import FilterField from './FilterField';
import ResultsSummary from './ResultsSummary';
import FilterMobile from './FilterMobile';
import FilterMobileChips from './FilterMobileChips';
import SortDropdown from './SortDropdown';
import {
  COVERAGE_TYPES,
  MedicarePlanType,
  PlanSearchCriteria,
  FilterOptions,
  ActiveSelections,
  FilterSelectionField,
} from './types';
import {
  selectAlexRecommendedPlan,
  selectMedApiUserParams,
  selectRecommendation,
} from 'slices/selectors';
import { usePreferredPlanYearSelection } from 'hooks/usePreferredPlanYearSelection';
import BackButton from 'app/cart/BackButton';
import ComparisonTray from 'components/plan/ComparisonTray';
import { seedComparison } from 'slices/comparisonSlice';
import PlanTabs from './PlanTabs';
import { setBrowseUserId } from 'utils/browseSession';
import { formatGapFilterLabel } from 'utils/formatters';
import { medSuppQuoteParamsFromUser } from 'utils/medSuppQuoteParams';
import * as Sentry from '@sentry/nextjs';

// Premiums sorted lowest to highest
const sortByPremium = (a: MedicareApiPlan, b: MedicareApiPlan) =>
  a.annual_premium - b.annual_premium;

// Medicare Star ratings sorted highest to lowest
const sortByStars = (a: MedicareApiPlan, b: MedicareApiPlan) =>
  b.star_ratings.overall - a.star_ratings.overall;

// ALEX plan score sorted highest to lowest (if they got a rec)
const sortByScore = (a: MedicareApiPlan, b: MedicareApiPlan) => {
  return b.picwell_score - a.picwell_score;
};

const combineComparators =
  (...comparators: Array<(a: MedicareApiPlan, b: MedicareApiPlan) => number>) =>
  (a: MedicareApiPlan, b: MedicareApiPlan) => {
    for (const compare of comparators) {
      const result = compare(a, b);
      if (result !== 0) return result;
    }
    return 0;
  };

const SORTERS = {
  score: combineComparators(sortByScore, sortByStars, sortByPremium),
  premium: combineComparators(sortByPremium, sortByScore, sortByStars),
  stars: combineComparators(sortByStars, sortByScore, sortByPremium),
};

const FILTER_SECTIONS = {
  network: 'networks',
  carrier: 'carriers',
  letter: 'letters',
} as const;

const RESET_SELECTIONS: ActiveSelections = {
  networks: [],
  carriers: [],
  letters: [],
};

const uniqueFilterOptions = (
  plans: MedicareApiPlan[],
  key: keyof MedicareApiPlan
): string[] => Array.from(new Set(plans.map((p) => p[key] as string))).sort();

const toggleSelection = (current: string[], value: string): string[] =>
  current.includes(value)
    ? current.filter((v) => v !== value)
    : [...current, value];

const matchesSelection = (filterSelections: string[], value: string): boolean =>
  filterSelections.length === 0 || filterSelections.includes(value);

// Get selected planType from query params, ma defaults to mapd because that's what the filter uses
const getSelectedPlanType = (typeParam: string | null): MedicarePlanType =>
  typeParam === 'gap' || typeParam === 'pdp' ? typeParam : 'mapd';

// Browse fetches use dummy drugs. Med Supp quotes use the member profile
// (age-65 January 1 fallback when DOB is missing). If ALEX already
// populated Redux, keep those results instead of letting a slower browse
// response clobber scores, premiums, and cart.
const resultsPlanYear = (results: RecommendationResults): number =>
  results.planYear ?? results.results[0]?.planYear ?? DEFAULT_PLAN_YEAR;

const resolveBrowsePayload = (
  fetched: MedicareApiPlan[],
  reduxResults: RecommendationResults,
  planYear: number
): { payload: RecommendationResults; writeRedux: boolean } => {
  const reduxPlanYear = resultsPlanYear(reduxResults);
  if (reduxResults.hasResults && reduxPlanYear === planYear) {
    return { payload: reduxResults, writeRedux: false };
  }
  return {
    payload: { hasResults: true, results: fetched, planYear },
    writeRedux: true,
  };
};

const EMPTY_RESULTS: RecommendationResults = {
  hasResults: false,
  results: [],
};

const cacheFromResults = (
  results: RecommendationResults
): Record<number, RecommendationResults> => {
  if (!results.hasResults) return {};
  const planYear = resultsPlanYear(results);
  return { [planYear]: { ...results, planYear } };
};

const resultsForYear = (
  reduxResults: RecommendationResults,
  cache: Record<number, RecommendationResults>,
  planYear: number
): RecommendationResults =>
  reduxResults.hasResults && resultsPlanYear(reduxResults) === planYear
    ? reduxResults
    : cache[planYear] ?? EMPTY_RESULTS;

const RecommendationPage = () => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const dispatch = useAppDispatch();
  const store = useAppStore();

  const location = useAppSelector(selectCurrentLocation);
  const recommendations = useAppSelector(selectRecommendation);
  const user = useAppSelector(selectMedApiUserParams);
  const alexRecommendedPlans = useAppSelector(selectAlexRecommendedPlan);
  const [planYear, choosePlanYear] = usePreferredPlanYearSelection();
  const selectedYear = Number(planYear);

  // Read by selectedYear, not availability.selectedPlanYear. After reload the
  // preferred year can update before the availability sync effect runs; using
  // the lagging availability year makes already-stored ALEX results look empty.
  const maRedux = useAppSelector((state) =>
    selectRecommendationMaPlans(state, selectedYear)
  );
  const pdpRedux = useAppSelector((state) =>
    selectRecommendationPdpPlans(state, selectedYear)
  );
  const gapRedux = useAppSelector((state) =>
    selectRecommendationGapPlans(state, selectedYear)
  );

  const filterYears = SUPPORTED_PLAN_YEARS.map(String);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [userId, setUserId] = useState<string>('');
  const [plans, setPlans] = useState<MedicareApiPlan[]>([]);

  // Year is kept out of this state. The shopping-year preference arrives
  // after mount, and a useState initial value would stay on the default.
  const [searchCriteria, setSearchCriteria] = useState<
    Omit<PlanSearchCriteria, 'year'>
  >({
    type: getSelectedPlanType(searchParams.get('type')),
    location,
  });

  // Cache each plan year independently. Redux may receive a personalized ALEX
  // response while a different Browse year is active; keeping both avoids
  // either response replacing the other.
  const [maCache, setMaCache] = useState(() => cacheFromResults(maRedux));
  const [pdpCache, setPdpCache] = useState(() => cacheFromResults(pdpRedux));
  const [gapCache, setGapCache] = useState(() => cacheFromResults(gapRedux));
  const maResults = resultsForYear(maRedux, maCache, selectedYear);
  const pdpResults = resultsForYear(pdpRedux, pdpCache, selectedYear);
  const gapResults = resultsForYear(gapRedux, gapCache, selectedYear);

  useEffect(() => {
    dispatch(setSelectedPlanYear(selectedYear));
  }, [dispatch, selectedYear]);

  const isMapd = searchCriteria.type === 'mapd';
  const isGap = searchCriteria.type === 'gap';
  const isPdp = searchCriteria.type === 'pdp';

  const [sortOrder, setSortOrder] = useState('premium');

  // Use cached filter options if they exist
  const [filterOptions, setFilterOptions] = useState<FilterOptions>(() => {
    const networks = maRedux.hasResults
      ? uniqueFilterOptions(maRedux.results, 'network')
      : [];
    const letters = gapRedux.hasResults
      ? uniqueFilterOptions(gapRedux.results, 'medicare_id')
      : [];
    const carriers = maRedux.hasResults
      ? uniqueFilterOptions(maRedux.results, 'carrier')
      : [];
    return { networks, letters, carriers };
  });

  const [activeSelections, setActiveSelections] =
    useState<ActiveSelections>(RESET_SELECTIONS);

  const sortOrderRef = useRef(sortOrder);

  useEffect(() => {
    sortOrderRef.current = sortOrder;
  }, [sortOrder]);

  // If has ALEX recommendation, show rec plans in comparison tray on load
  useEffect(() => {
    if (!alexRecommendedPlans) return;
    alexRecommendedPlans.forEach((plan) => {
      const planType = plan.type === 'ma' ? 'mapd' : plan.type;
      dispatch(seedComparison({ plan, planType }));
    });
  }, [alexRecommendedPlans]); // eslint-disable-line react-hooks/exhaustive-deps

  const plansContainerRef = useRef<HTMLDivElement>(null);
  const isFirstPlansRender = useRef(true);

  // Fade in plan cards if filter selections or sort order changes
  useEffect(() => {
    // Skip animation on initial render
    if (isFirstPlansRender.current) {
      isFirstPlansRender.current = false;
      return;
    }
    const el = plansContainerRef.current;
    if (!el) return;
    el.style.animation = 'none';
    void el.offsetHeight;
    el.style.animation = '';
  }, [activeSelections, sortOrder]);

  const handleYearChange = useCallback(
    (year: string) => {
      choosePlanYear(year);
    },
    [choosePlanYear]
  );

  const handleSortChange = useCallback(
    (newOrder: string) => {
      const reordered = plans.toSorted(
        SORTERS[newOrder as keyof typeof SORTERS]
      );

      setPlans(reordered);
      setSortOrder(newOrder);
    },
    [plans]
  );

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLFormElement>) => {
      const input = e.target as unknown as HTMLInputElement;
      const filterSection =
        FILTER_SECTIONS[input.name as keyof typeof FILTER_SECTIONS];

      setActiveSelections((prev) => ({
        ...prev,
        [filterSection]: toggleSelection(prev[filterSection], input.value),
      }));
    },
    []
  );

  const removeSelection = (field: FilterSelectionField, value: string) => {
    setActiveSelections((prev) => ({
      ...prev,
      [field]: prev[field].filter((item) => item !== value),
    }));
  };

  // Filter and sort plans by selection(s);
  useEffect(() => {
    const activeTypeResults = isMapd
      ? maResults.results
      : isGap
      ? gapResults.results
      : pdpResults.results;

    if (activeTypeResults.length === 0) return;

    const filtered = activeTypeResults.filter((plan) => {
      const networkMatch =
        !isMapd || matchesSelection(activeSelections.networks, plan.network);

      const letterMatch =
        !isGap || matchesSelection(activeSelections.letters, plan.medicare_id);

      const carrierMatch = matchesSelection(
        activeSelections.carriers,
        plan.carrier
      );

      return networkMatch && carrierMatch && letterMatch;
    });

    const sorter = SORTERS[sortOrder as keyof typeof SORTERS];
    queueMicrotask(() =>
      setPlans(sorter ? [...filtered].sort(sorter) : filtered)
    );
  }, [
    activeSelections,
    sortOrder,
    searchCriteria,
    isMapd,
    isGap,
    maResults.results,
    gapResults.results,
    pdpResults.results,
  ]);

  useEffect(() => {
    async function prepUser() {
      if (user.id) {
        await updateMedApiUser(user);
        setUserId(user.id);
      } else {
        // New User
        const tmpUser = await createBrowseUser(searchCriteria.location);
        setBrowseUserId(tmpUser.id);
        setUserId(tmpUser.id);
      }
    }

    if (searchCriteria.location.zipcode) {
      // Make sure we have a zip code
      prepUser();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let stale = false;

    async function fetchPlans() {
      setIsLoading(true);
      setPlans([]);

      let plans: MedicareApiPlan[];

      try {
        if (isGap) {
          if (gapResults.hasResults) {
            plans = gapResults.results;
          } else {
            // Premiums are age-, sex-, and tobacco-rated as of January 1 of
            // the plan year. "Today minus 65 years" is still 64 on that date
            // for the rest of the year, and it ignores a logged-in profile,
            // so the list disagreed with plan details.
            plans = await recommendMedSuppPlansAction(
              userId,
              selectedYear,
              searchCriteria.location,
              medSuppQuoteParamsFromUser(selectUser(store.getState()))
            );

            const reduxResults = selectRecommendationGapPlans(
              store.getState(),
              selectedYear
            );
            if (reduxResults.hasResults) {
              const reduxYear = resultsPlanYear(reduxResults);
              setGapCache((current) => ({
                ...current,
                [reduxYear]: { ...reduxResults, planYear: reduxYear },
              }));
            }
            const { payload, writeRedux } = resolveBrowsePayload(
              plans,
              reduxResults,
              selectedYear
            );
            setGapCache((current) => ({
              ...current,
              [selectedYear]: payload,
            }));
            if (writeRedux) dispatch(setGapPlans(payload));
            plans = payload.results;
          }
        } else if (isPdp) {
          if (pdpResults.hasResults) {
            plans = pdpResults.results;
          } else {
            plans = await recommendPdpPlansAction(
              userId,
              selectedYear,
              searchCriteria.location,
              [] // No drugs in the browse rec
            );

            const reduxResults = selectRecommendationPdpPlans(
              store.getState(),
              selectedYear
            );
            if (reduxResults.hasResults) {
              const reduxYear = resultsPlanYear(reduxResults);
              setPdpCache((current) => ({
                ...current,
                [reduxYear]: { ...reduxResults, planYear: reduxYear },
              }));
            }
            const { payload, writeRedux } = resolveBrowsePayload(
              plans,
              reduxResults,
              selectedYear
            );
            setPdpCache((current) => ({
              ...current,
              [selectedYear]: payload,
            }));
            if (writeRedux) dispatch(setPdpPlans(payload));
            plans = payload.results;
          }
        } else {
          if (maResults.hasResults) {
            plans = maResults.results;
          } else {
            plans = await recommendMaPlansAction(
              userId,
              selectedYear,
              searchCriteria.location,
              [] // No drugs in the browse rec
            );

            const reduxResults = selectRecommendationMaPlans(
              store.getState(),
              selectedYear
            );
            if (reduxResults.hasResults) {
              const reduxYear = resultsPlanYear(reduxResults);
              setMaCache((current) => ({
                ...current,
                [reduxYear]: { ...reduxResults, planYear: reduxYear },
              }));
            }
            const { payload, writeRedux } = resolveBrowsePayload(
              plans,
              reduxResults,
              selectedYear
            );
            setMaCache((current) => ({
              ...current,
              [selectedYear]: payload,
            }));
            if (writeRedux) dispatch(setMaPlans(payload));
            plans = payload.results;
          }
        }
      } catch (error) {
        // Prefer already-stored results over wiping the page empty. A failed
        // browse must still clear the spinner, but must not clobber ALEX
        // results that were already in Redux for this year.
        Sentry.captureException(error);
        const stored = (
          isGap
            ? selectRecommendationGapPlans
            : isPdp
            ? selectRecommendationPdpPlans
            : selectRecommendationMaPlans
        )(store.getState(), selectedYear);
        plans = stored.hasResults ? stored.results : [];
      }

      // A newer plan-type switch superseded this fetch while it was in
      // flight (see cleanup below) — cache the results above for reuse,
      // but don't let this stale response overwrite what's on screen now.
      if (stale) return;

      const networks = isMapd ? uniqueFilterOptions(plans, 'network') : [];
      const letters = isGap ? uniqueFilterOptions(plans, 'medicare_id') : [];
      const carriers = uniqueFilterOptions(plans, 'carrier');

      setFilterOptions({ networks, carriers, letters });
      setActiveSelections(RESET_SELECTIONS);

      const currentOrder = sortOrderRef.current;
      const sorter = SORTERS[currentOrder as keyof typeof SORTERS];
      setPlans(sorter ? [...plans].sort(sorter) : plans);
      setIsLoading(false);
    }

    if (searchCriteria.location.zipcode) {
      if (userId) {
        fetchPlans();
      }
    } else {
      router.push('/browse');
    }

    return () => {
      stale = true;
    };
  }, [searchCriteria, selectedYear, userId, router]); // eslint-disable-line react-hooks/exhaustive-deps

  const title = `Compare ${
    COVERAGE_TYPES[searchCriteria.type as keyof typeof COVERAGE_TYPES]?.name ??
    ''
  } Plans`;

  const loadingMessage = `Searching for ${
    COVERAGE_TYPES[searchCriteria.type as keyof typeof COVERAGE_TYPES]?.name
  } plans...`;

  if (!searchCriteria.location.zipcode) {
    return null;
  }

  return (
    <div className={styles.browseResultsContainer}>
      <PlanTabs
        activeType={searchCriteria.type}
        onTypeChange={(type) => {
          setSearchCriteria((prev) => ({ ...prev, type }));
          setActiveSelections(RESET_SELECTIONS);
        }}
      />
      <div className={styles.browseWrapper}>
        <div className={styles.browseResults}>
          <div className={styles.headerContainer}>
            <BackButton className={styles.backButton} />
            <div className={styles.title}>
              <h1>{title}</h1>
              <ResultsSummary
                zipcode={searchCriteria.location.zipcode}
                plansDisplayed={plans.length}
                planType={searchCriteria.type}
                isLoading={isLoading}
              />
            </div>
            <div className={styles.actions}>
              <FilterMobile
                activeType={searchCriteria.type}
                activeNetworks={activeSelections.networks}
                networkOptions={filterOptions.networks}
                activeCarriers={activeSelections.carriers}
                carrierOptions={filterOptions.carriers}
                activeLetters={activeSelections.letters}
                letterOptions={filterOptions.letters}
                years={filterYears}
                selectedYear={planYear}
                onYearChange={handleYearChange}
                onApply={(networks, carriers, letters) => {
                  setActiveSelections({ networks, carriers, letters });
                }}
              />
              <SortDropdown
                active={sortOrder}
                type={searchCriteria.type}
                updateSorting={handleSortChange}
              />
            </div>
            <FilterMobileChips
              planType={searchCriteria.type}
              activeNetworks={activeSelections.networks}
              activeCarriers={activeSelections.carriers}
              activeLetters={activeSelections.letters}
              onRemove={removeSelection}
              onReset={() => setActiveSelections(RESET_SELECTIONS)}
              formatLetterLabel={formatGapFilterLabel}
            />
          </div>

          {/* Left column */}
          <aside className={styles.filterContainer} aria-label="Plan filters">
            <div className={styles.filterHeader}>
              <h4>Filter Plans</h4>
              <button
                className={styles.resetFiltersBtn}
                onClick={() => setActiveSelections(RESET_SELECTIONS)}
              >
                Reset
              </button>
            </div>
            {filterYears.length > 1 && (
              <PlanYearToggle
                years={filterYears}
                selectedYear={planYear}
                onChange={handleYearChange}
              />
            )}
            {isMapd && (
              <FilterField
                title="Network type"
                name="network"
                options={filterOptions.networks}
                active={activeSelections.networks}
                onChange={handleFilterChange}
              />
            )}

            {isGap && (
              <FilterField
                title="Plan letter"
                name="letter"
                options={filterOptions.letters}
                active={activeSelections.letters}
                onChange={handleFilterChange}
                formatLabel={formatGapFilterLabel}
              />
            )}

            <FilterField
              title="Carriers"
              name="carrier"
              options={filterOptions.carriers}
              active={activeSelections.carriers}
              onChange={handleFilterChange}
            />
            <CTASideAd />
          </aside>

          {/* Main content */}
          <div
            ref={plansContainerRef}
            className={styles.plansContainer}
            aria-busy={isLoading}
          >
            {isLoading && <Loading message={loadingMessage} />}
            {!isLoading && plans.length === 0 && <p>(No plans found)</p>}
            {plans.map((plan) => {
              return (
                <PlanCard
                  key={planIdentityKey(plan)}
                  plan={plan}
                  context="browse"
                  recommendations={recommendations}
                  userId={userId}
                  location={location}
                />
              );
            })}
          </div>
        </div>
        <ComparisonTray
          planType={searchCriteria.type}
          year={planYear}
          location={location}
          userId={userId}
        />
      </div>
    </div>
  );
};

export default RecommendationPage;
